import { useState } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { eur, num, showMoney } from "@/lib/format";
import { ChevronDown, ChevronRight, Palette, BadgeEuro, LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PriceHistoryDialog } from "@/components/materials/PriceHistoryDialog";

const cheapest = (variants) => {
  const priced = variants.filter((v) => v.cost_per_unit > 0);
  if (!priced.length) return null;
  const best = priced.reduce((a, b) => (b.cost_per_unit < a.cost_per_unit ? b : a));
  const suppliers = new Set(priced.map((v) => v.supplier).filter(Boolean)).size;
  return { ...best, suppliers };
};

const metersByColor = (variants) => {
  const m = {};
  variants.forEach((v) => { const c = v.color || "Senza colore"; m[c] = (m[c] || 0) + (v.quantity || 0); });
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
};

export const FabricTypeHeader = ({ type, groups }) => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(false);
  const variants = groups.flatMap((g) => g.variants);
  const total = variants.reduce((s, v) => s + (v.quantity || 0), 0);
  const best = cheapest(variants);
  const colors = metersByColor(variants);
  const slug = type.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <>
      <TableRow className="cursor-pointer bg-primary/5 hover:bg-primary/10" onClick={() => setOpen(!open)} data-testid={`fabric-type-${slug}`}>
        <TableCell colSpan={5} className="py-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-1 font-display text-sm font-semibold text-primary">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{type}
            </span>
            <span className="text-xs text-muted-foreground">{groups.length} {groups.length === 1 ? "tessuto" : "tessuti"} · {variants.length} varianti · <b className="text-foreground">{num(total)} m</b></span>
            {best && showMoney() && (
              <span className="flex items-center gap-1 text-xs text-emerald-400" data-testid={`fabric-type-${slug}-cheapest`}>
                <BadgeEuro className="h-3.5 w-3.5" />
                {best.suppliers > 1 ? "Più conveniente" : "Prezzo"}: <b>{best.supplier || "-"}</b> · {eur(best.cost_per_unit)}/m
                <span className="text-muted-foreground">({best.name}{best.color ? `, ${best.color}` : ""})</span>
              </span>
            )}
            <span className="flex items-center gap-1 text-xs text-muted-foreground"><Palette className="h-3.5 w-3.5" />{open ? "Nascondi" : "Vedi"} metri per colore</span>
            {best && showMoney() && (
              <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={(e) => { e.stopPropagation(); setHistory(true); }} data-testid={`fabric-type-${slug}-history-btn`}>
                <LineChart className="mr-1 h-3 w-3" />Storico prezzi
              </Button>
            )}
          </div>
          {showMoney() && <div onClick={(e) => e.stopPropagation()}><PriceHistoryDialog type={type} open={history} onOpenChange={setHistory} /></div>}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-primary/5 hover:bg-primary/5" data-testid={`fabric-type-${slug}-colors`}>
          <TableCell colSpan={5} className="pb-3 pt-0">
            <div className="flex flex-wrap gap-2 pl-5">
              {colors.map(([c, q]) => (
                <span key={c} className={`rounded-md border px-2 py-1 text-xs ${q > 0 ? "border-border" : "border-rose-500/40 text-rose-400"}`} data-testid={`fabric-type-${slug}-color-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {c} <b className="ml-1">{num(q)} m</b>
                </span>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
